import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  SimpleGrid,
  Container,
  Heading,
  useColorModeValue,
  Spinner,
  Center,
  IconButton,
  Tooltip,
  Alert,
  AlertIcon,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Divider,
  Code,
  FormControl,
  FormLabel,
  Input,
  Textarea
} from '@chakra-ui/react';
import { 
  FiGitBranch, 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiRefreshCw,
  FiClock,
  FiUser,
  FiRotateCcw,
  FiEye,
  FiCheck
} from 'react-icons/fi';
import { ContentManagementService, type ContentVersion } from '../services/contentManagementService';
import { useAuth } from '../contexts/AuthContext';
import IconWrapper from './IconWrapper';

interface ContentVersioningProps {
  contentId: string;
  showHeader?: boolean;
}

const ContentVersioning: React.FC<ContentVersioningProps> = ({
  contentId,
  showHeader = true
}) => {
  const { user } = useAuth();
  const toast = useToast();
  const [versions, setVersions] = useState<ContentVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
  const [isRollbackModalOpen, setIsRollbackModalOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<ContentVersion | null>(null);
  const [isViewingVersion, setIsViewingVersion] = useState(false);

  // Form states
  const [versionTitle, setVersionTitle] = useState('');
  const [versionDescription, setVersionDescription] = useState('');
  const [versionChanges, setVersionChanges] = useState('');

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    if (contentId) {
      loadVersions();
    }
  }, [contentId]);

  const loadVersions = async () => {
    try {
      setIsLoading(true);
      const versionsData = await ContentManagementService.getContentVersions(contentId);
      setVersions(versionsData);
    } catch (error) {
      console.error('Error loading versions:', error);
      toast({
        title: 'Error loading versions',
        description: 'Failed to load content versions. Please try again.',
        status: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateVersion = async () => {
    if (!versionTitle.trim() || !versionDescription.trim() || !versionChanges.trim()) {
      toast({
        title: 'All fields required',
        description: 'Please fill in all required fields.',
        status: 'error'
      });
      return;
    }

    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in to create versions.',
        status: 'error'
      });
      return;
    }

    try {
      setIsCreating(true);
      const changes = versionChanges.split('\n').filter(change => change.trim());
      
      await ContentManagementService.createContentVersion(
        contentId,
        versionTitle,
        versionDescription,
        changes,
        user.uid
      );
      
      toast({
        title: 'Version created',
        description: 'Content version has been created successfully.',
        status: 'success'
      });
      
      setVersionTitle('');
      setVersionDescription('');
      setVersionChanges('');
      setIsVersionModalOpen(false);
      await loadVersions();
    } catch (error: any) {
      console.error('Error creating version:', error);
      toast({
        title: 'Error creating version',
        description: error.message || 'Failed to create version.',
        status: 'error'
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleRollbackVersion = async () => {
    if (!selectedVersion) return;

    try {
      await ContentManagementService.rollbackToVersion(contentId, selectedVersion.id);
      
      toast({
        title: 'Version rolled back',
        description: 'Content has been rolled back to the selected version.',
        status: 'success'
      });
      
      setIsRollbackModalOpen(false);
      setSelectedVersion(null);
      await loadVersions();
    } catch (error: any) {
      console.error('Error rolling back version:', error);
      toast({
        title: 'Error rolling back version',
        description: error.message || 'Failed to rollback version.',
        status: 'error'
      });
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString() + ' ' + dateObj.toLocaleTimeString();
  };

  const getVersionColor = (version: ContentVersion) => {
    if (version.isActive) return 'green';
    if (version.version === 1) return 'blue';
    return 'gray';
  };

  const renderVersionRow = (version: ContentVersion) => {
    return (
      <Tr key={version.id}>
        <Td>
          <HStack gap={3}>
            <Badge
              colorScheme={getVersionColor(version)}
              fontSize="sm"
              px={2}
              py={1}
            >
              v{version.version}
            </Badge>
            {version.isActive && (
              <Badge colorScheme="green" fontSize="xs">
                <IconWrapper icon={FiCheck} size={10} style={{ marginRight: '4px' }} />
                Active
              </Badge>
            )}
          </HStack>
        </Td>
        <Td>
          <VStack align="start" gap={1}>
            <Text fontWeight="medium" color="white" noOfLines={1}>
              {version.title}
            </Text>
            <Text fontSize="xs" color="gray.400" noOfLines={1}>
              {version.description}
            </Text>
          </VStack>
        </Td>
        <Td>
          <Text fontSize="sm" color="gray.300">
            {formatDate(version.createdAt)}
          </Text>
        </Td>
        <Td>
          <Text fontSize="sm" color="gray.400">
            {version.createdBy}
          </Text>
        </Td>
        <Td>
          <HStack gap={1}>
            <Tooltip label="View version details">
              <IconButton
                size="sm"
                variant="ghost"
                icon={<IconWrapper icon={FiEye} size={14} />}
                onClick={() => {
                  setSelectedVersion(version);
                  setIsViewingVersion(true);
                }}
                aria-label="View version"
              />
            </Tooltip>
            {!version.isActive && (
              <Tooltip label="Rollback to this version">
                <IconButton
                  size="sm"
                  variant="ghost"
                  colorScheme="orange"
                  icon={<IconWrapper icon={FiRotateCcw} size={14} />}
                  onClick={() => {
                    setSelectedVersion(version);
                    setIsRollbackModalOpen(true);
                  }}
                  aria-label="Rollback version"
                />
              </Tooltip>
            )}
          </HStack>
        </Td>
      </Tr>
    );
  };

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="lg" color="purple.400" />
      </Center>
    );
  }

  return (
    <Container maxW="container.xl">
      <VStack gap={6} align="stretch">
        {showHeader && (
          <HStack justify="space-between">
            <Heading size="lg" color="white">
              <IconWrapper icon={FiGitBranch} size={24} style={{ marginRight: '8px' }} />
              Content Versioning
            </Heading>
            <HStack gap={2}>
              {user && (
                <Button
                  colorScheme="purple"
                  leftIcon={<IconWrapper icon={FiPlus} size={16} />}
                  onClick={() => setIsVersionModalOpen(true)}
                >
                  Create Version
                </Button>
              )}
              <Tooltip label="Refresh versions">
                <IconButton
                  aria-label="Refresh"
                  icon={<IconWrapper icon={FiRefreshCw} size={16} />}
                  onClick={loadVersions}
                  variant="ghost"
                  colorScheme="purple"
                />
              </Tooltip>
            </HStack>
          </HStack>
        )}

        {/* Version Statistics */}
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
          <Box bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
            <HStack gap={2}>
              <IconWrapper icon={FiGitBranch} size={20} color="blue.400" />
              <VStack align="start" gap={0}>
                <Text fontSize="2xl" fontWeight="bold" color="white">
                  {versions.length}
                </Text>
                <Text fontSize="sm" color="gray.400">
                  Total Versions
                </Text>
              </VStack>
            </HStack>
          </Box>
          
          <Box bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
            <HStack gap={2}>
              <IconWrapper icon={FiCheck} size={20} color="green.400" />
              <VStack align="start" gap={0}>
                <Text fontSize="2xl" fontWeight="bold" color="white">
                  {versions.filter(v => v.isActive).length}
                </Text>
                <Text fontSize="sm" color="gray.400">
                  Active Version
                </Text>
              </VStack>
            </HStack>
          </Box>
          
          <Box bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
            <HStack gap={2}>
              <IconWrapper icon={FiClock} size={20} color="purple.400" />
              <VStack align="start" gap={0}>
                <Text fontSize="2xl" fontWeight="bold" color="white">
                  {versions.length > 0 ? versions[0].version : 0}
                </Text>
                <Text fontSize="sm" color="gray.400">
                  Latest Version
                </Text>
              </VStack>
            </HStack>
          </Box>
        </SimpleGrid>

        {/* Versions Table */}
        {versions.length === 0 ? (
          <Center py={8}>
            <VStack gap={2}>
              <Text color="gray.500">No versions found</Text>
              <Text fontSize="sm" color="gray.400">
                Create your first version to track content changes
              </Text>
            </VStack>
          </Center>
        ) : (
          <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} overflow="hidden">
            <TableContainer>
              <Table variant="simple">
                <Thead bg="gray.700">
                  <Tr>
                    <Th color="white">Version</Th>
                    <Th color="white">Title & Description</Th>
                    <Th color="white">Created</Th>
                    <Th color="white">Author</Th>
                    <Th color="white">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {versions.map(renderVersionRow)}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Create Version Modal */}
        <Modal isOpen={isVersionModalOpen} onClose={() => setIsVersionModalOpen(false)}>
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white" maxW="2xl">
            <ModalHeader>Create New Version</ModalHeader>
            <ModalBody>
              <VStack gap={4}>
                <FormControl>
                  <FormLabel>Version Title</FormLabel>
                  <Input
                    placeholder="Enter version title"
                    value={versionTitle}
                    onChange={(e) => setVersionTitle(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ bg: 'gray.600' }}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    placeholder="Enter version description"
                    value={versionDescription}
                    onChange={(e) => setVersionDescription(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ bg: 'gray.600' }}
                    rows={3}
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Changes (one per line)</FormLabel>
                  <Textarea
                    placeholder="Enter changes made in this version (one per line)"
                    value={versionChanges}
                    onChange={(e) => setVersionChanges(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ bg: 'gray.600' }}
                    rows={4}
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setIsVersionModalOpen(false)}>
                Cancel
              </Button>
              <Button
                colorScheme="purple"
                onClick={handleCreateVersion}
                isLoading={isCreating}
                loadingText="Creating..."
              >
                Create Version
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* View Version Modal */}
        <Modal isOpen={isViewingVersion} onClose={() => setIsViewingVersion(false)}>
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white" maxW="2xl">
            <ModalHeader>
              Version {selectedVersion?.version} Details
            </ModalHeader>
            <ModalBody>
              {selectedVersion && (
                <VStack align="stretch" gap={4}>
                  <Box>
                    <Text fontWeight="bold" color="white" mb={2}>
                      {selectedVersion.title}
                    </Text>
                    <Text color="gray.400" mb={4}>
                      {selectedVersion.description}
                    </Text>
                  </Box>
                  
                  <Divider />
                  
                  <Box>
                    <Text fontWeight="bold" color="white" mb={2}>
                      Changes Made:
                    </Text>
                    <VStack align="stretch" gap={2}>
                      {selectedVersion.changes.map((change, index) => (
                        <HStack key={index} align="start" gap={2}>
                          <Text color="purple.400" fontSize="sm">•</Text>
                          <Text fontSize="sm" color="gray.300">
                            {change}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                  
                  <Divider />
                  
                  <HStack justify="space-between" fontSize="sm" color="gray.400">
                    <Text>Created: {formatDate(selectedVersion.createdAt)}</Text>
                    <Text>By: {selectedVersion.createdBy}</Text>
                  </HStack>
                </VStack>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onClick={() => setIsViewingVersion(false)}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Rollback Confirmation Modal */}
        <Modal isOpen={isRollbackModalOpen} onClose={() => setIsRollbackModalOpen(false)}>
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white">
            <ModalHeader>Rollback to Version {selectedVersion?.version}</ModalHeader>
            <ModalBody>
              <Alert status="warning">
                <AlertIcon />
                <AlertDescription>
                  Are you sure you want to rollback to version {selectedVersion?.version}? 
                  This will revert the content to this previous state. This action cannot be undone.
                </AlertDescription>
              </Alert>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setIsRollbackModalOpen(false)}>
                Cancel
              </Button>
              <Button colorScheme="orange" onClick={handleRollbackVersion}>
                Rollback
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Container>
  );
};

export default ContentVersioning;
